/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import axios from "axios";
import { locale as dayjsLocale } from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import CheckIcon from "@krea/customer-features/images/svg/lang-check.svg";
import LangIcon from "@krea/customer-features/images/svg/lang.svg";
import {
  getLanguageFromCookie,
  setLanguageInCookie,
} from "@krea/customer-features/services/languageService";
import { settings } from "@krea/customer-features/settings";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetUserData } from "@krea/customer-features/store/user/hooks/queries/useGetUserData";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";
import { CountryCode } from "@krea/shared/types/common";

import styles from "./Language.module.scss";

interface IIconProps {
  src: string;
  className?: string;
}

const Icon = ({ src, className }: IIconProps) => {
  return (
    <div className={twMerge(styles.icon, "tw-relative", className)}>
      {src && <SVG className="tw-absolute tw-h-auto tw-w-full" src={src} />}
    </div>
  );
};

interface ILanguageProps {
  className?: string;
}

// TODO: Refactor this component to use only tailwind. It has a lot of animation and custom styles that were hard to replicate in a short time.
const Language = ({ className }: ILanguageProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(
    getLanguageFromCookie() ?? i18n.language,
  );

  const { data: user } = useGetUserData();

  const countryCode = settings.countryCode;

  const locales = useMemo(() => {
    const l = ["sv", "en"];

    if (countryCode === CountryCode.FI) {
      l.unshift("fi");
    }

    return l;
  }, [countryCode]);

  const capitalize = (str: string) => {
    return str.replace(/(^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });
  };

  const setLanguageOnBackend = async (lang: string) => {
    return axios.post(`/api/v1/me/language`, {
      preferred_language: lang,
    });
  };

  useEffect(() => {
    if (currentLang) {
      try {
        i18n.changeLanguage(currentLang);
        setLanguageInCookie(currentLang);
        dayjsLocale(currentLang);
      } catch (e) {
        console.error("Error changing language", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang]);

  useEffect(() => {
    if (user?.preferredLanguage) {
      setCurrentLang(user.preferredLanguage.toLowerCase());
    }
  }, [user]);

  const handleLanguageChange = async (lang: string) => {
    if (isOpen) {
      setCurrentLang(lang);

      if (!isEmpty(user)) {
        await setLanguageOnBackend(lang);
      }

      window.location.reload();
    }
  };

  return (
    <div
      className={twMerge(styles.lang, "tw-relative xl:tw-ml-6", className)}
      onMouseEnter={() => setTimeout(() => setIsOpen(true), 300)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div
        className={twMerge(
          styles.current,
          "tw-flex tw-items-center tw-px-3 tw-font-bold tw-relative",
        )}
      >
        <Icon src={LangIcon} />
        <div className="tw-ml-2 xl:tw-ml-3">{capitalize(currentLang)}</div>
      </div>
      <ul
        className={twMerge(
          styles.list,
          "tw-list-none tw-m-0 tw-absolute tw-bg-white tw-text-black tw-rounded tw-w-full tw-overflow-hidden tw-border tw-shadow-md",
        )}
      >
        {locales.map((item, index) => (
          <li
            aria-hidden
            className={twMerge(
              styles.item,
              "tw-flex tw-items-center tw-px-3 tw-relative hover:tw-bg-purple-150/70",
              {
                "tw-text-primary": currentLang === item,
              },
            )}
            key={index}
            onClick={() => {
              handleLanguageChange(item);
            }}
          >
            <div className={styles.checkboxWrapper}>
              <Icon
                className={styles.checkbox}
                src={currentLang === item ? CheckIcon : ""}
              />
            </div>

            <Text
              className={twMerge(
                "tw-font-bold tw-text-center tw-ml-2 xl:tw-ml-3",
              )}
            >
              {capitalize(item)}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Language;
